import type {
  ApiFetchSiteStatusPort,
  SiteNoticeTexts,
  SiteNoticeVariant,
  SiteStatus,
} from '~/handlers/ports/api-fetch-site-status.port';
import { getApostatudoApiUrl } from '~/utils/apostatudo/get-apostatudo-api-url';

const NOTICE_VARIANTS: SiteNoticeVariant[] = [
  'home',
  'games',
  'deposit',
  'register',
  'checkin',
  'roulette',
];

// Curto de propósito: a busca roda no plugin crítico, que segura a abertura do
// site. Estourou, vale o último status bom (ou o fallback pausado).
const TIMEOUT_MS = 4000;

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

function mapNotice(raw: any): SiteNoticeTexts {
  const notice: SiteNoticeTexts = {
    title: text(raw?.title),
    message: text(raw?.message),
    reassurance: text(raw?.reassurance),
    dismissLabel: text(raw?.dismiss_label),
  };

  // Tira as chaves vazias: o merge com o padrão do front é campo a campo.
  return Object.fromEntries(
    Object.entries(notice).filter(([, value]) => value !== undefined),
  );
}

// Status das operações do site (ENG-915): rota pública e global do core, sem
// token. Recusa resposta sem os booleanos em vez de ler ausência como "ligado" —
// um payload quebrado não pode reabrir o site.
export class ApostatudoApiFetchSiteStatusAdapter
  implements ApiFetchSiteStatusPort
{
  async handle(): Promise<SiteStatus> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    const response = await fetch(`${getApostatudoApiUrl()}/site-status`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    }).finally(() => clearTimeout(timeoutId));

    if (!response.ok) {
      throw new Error(`Site status failed: ${response.status}`);
    }

    const { data } = await response.json();

    const flags = [
      data?.home_notice_enabled,
      data?.games_enabled,
      data?.deposits_enabled,
      data?.registrations_enabled,
      data?.daily_check_in_enabled,
      data?.roulette_enabled,
    ];

    if (flags.some((flag) => typeof flag !== 'boolean')) {
      throw new Error('Site status payload is missing a switch');
    }

    const notices: SiteStatus['notices'] = {};

    for (const variant of NOTICE_VARIANTS) {
      const notice = mapNotice(data?.notices?.[variant]);

      if (Object.keys(notice).length > 0) {
        notices[variant] = notice;
      }
    }

    return {
      homeNoticeEnabled: data.home_notice_enabled,
      gamesEnabled: data.games_enabled,
      depositsEnabled: data.deposits_enabled,
      registrationsEnabled: data.registrations_enabled,
      dailyCheckInEnabled: data.daily_check_in_enabled,
      rouletteEnabled: data.roulette_enabled,
      notices,
    };
  }
}
